import { Box, Divider, Text, TextButton, TooltipCommonProps } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ChevronLeftLargeSmall } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { TLocalesKeys } from '../../../../types';
import { logBackButtonAction } from '../../../bi';
import { selectArticle } from '../../../common/selectors/helpCenter';
import { Page } from '../../../common/types';
import { renderModel } from '../../../util/renderModel';
import { articleSidebarDataHooks } from './ArticleSidebar.dataHooks';
import { ArticleSidebarActions } from './ArticleSidebarActions';

type ArticleSidebarHeaderProps = {
  subtitleText: string | undefined;
  showBackButton: boolean;
};

const titleTooltipProps: TooltipCommonProps = {
  size: 'small',
  placement: 'bottom',
  maxWidth: '72%',
};

const isMobile = renderModel.isMobile();

export const ArticleSidebarHeader: FC<ArticleSidebarHeaderProps> = ({ subtitleText, showBackButton }) => {
  const store = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const article = useSelector(selectArticle);

  const goBack = useCallback(() => {
    if (!article) {
      return;
    }

    const state = store.getState();
    logBackButtonAction(state, { currentPage: Page.Article });
    navigate(-1);
  }, [store, article, navigate]);

  if (showBackButton) {
    return (
      <>
        <Box maxWidth="836px" width="100%" direction="vertical" padding={isMobile ? '12px 18px' : 3} gap={1}>
          <Box width="100%" align="space-between" verticalAlign="middle">
            <TextButton
              size="tiny"
              weight="normal"
              onClick={goBack}
              prefixIcon={<ChevronLeftLargeSmall />}
              dataHook={articleSidebarDataHooks.backButton()}
            >
              {t<TLocalesKeys>('back_widget')}
            </TextButton>
            <Box marginLeft="auto">
              <ArticleSidebarActions />
            </Box>
          </Box>
          {subtitleText && (
            <Text weight="bold" size="medium" ellipsis tooltipProps={titleTooltipProps}>
              {subtitleText}
            </Text>
          )}
        </Box>
        {subtitleText && <Divider />}
      </>
    );
  }

  return (
    <>
      <Box maxWidth="836px" width="100%" padding={3} align="space-between" verticalAlign="middle" gap={2}>
        {subtitleText && (
          <Text weight="bold" size="medium" ellipsis tooltipProps={titleTooltipProps}>
            {subtitleText}
          </Text>
        )}
        <Box marginLeft="auto">
          <ArticleSidebarActions />
        </Box>
      </Box>
      {subtitleText && <Divider />}
    </>
  );
};
